/*
 * Copyright (c) Nexign, JSC, 2020-2025.
 */
/**
 * Product Offerings Sagas for finish step
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.37.0
 * @since 1.10.0
 */

import { createElement } from 'react';

import set from 'lodash/set';
import moment from 'moment';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { ModalMultipleOrders, TModalMultipleOrdersProps } from '@/main/modules/modal/components/ModalMultipleOrders';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { EProductsPageFrom } from '@/main/pages/PageProducts/tariffsAndServicesConstants';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import {
    API_HOSTNAME,
    PRODUCT_OFFERINGS_CAMPAIGN_SUBSCRIBERS_CHECK,
    PRODUCT_OFFERINGS_CREATE_INQUIRY,
    PRODUCT_OFFERINGS_SUBSCRIBERS_CHECK,
} from '@modules/app/Api';
import { EHttpMethod } from '@modules/app/appConstants';
import { makeSelectBreadCrumbsData } from '@modules/breadCrumbs/breadCrumbsSelectors';
import { breadCrumbGoBack } from '@modules/breadCrumbs/utils/breadCrumbsGoBack';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardProductOfferings,
    makeSelectWizardTranslation,
} from '@modules/internationalization/intSelectors';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { wizardReportStorageRestoreSaga } from '@modules/wizard/components/WizardReport/wizardReportSagas';
import { makeSelectWizardReportOrderParams } from '@modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportOrderParams } from '@modules/wizard/components/WizardReport/wizardReportTypes';
import { wizardFinishAction, wizardStorageUpdateStepAction } from '@modules/wizard/wizardActions';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { makeSelectWizardBasketSnapshotHeader, makeSelectWizardName } from '@modules/wizard/wizardSelectors';
import { TWizardMetaSuccessAction, TWizardNextSuccessAction } from '@modules/wizard/wizardTypes';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';

import { checkResponseErrorWithModal, requestSaga } from '../../sagas/requestSaga';
import { makeSelectFeatureEnabled, makeSelectIsFeatureWithVersionEnabled } from '../PageAuth/selectors/authSelectors';
import { makeSelectProductsPageFrom } from '../PageProducts/tariffsAndServicesSelectors';
import ProductOfferingsModalSuccess from './components/ProductOfferingsModalSuccess';
import ProductOfferingsModalSuccessInquiry from './components/ProductOfferingsModalSuccessInquiry';
import ProductOfferingsModalSuggestInquiry from './components/ProductOfferingsModalSuggestInquiry';
import {
    productOfferingsCreateInquiryFailure,
    productOfferingsCreateInquirySuccess,
    productOfferingsGetSubscribersCheckListFailure,
    productOfferingsGetSubscribersCheckListSuccess,
    productOfferingsSetParamsAction,
    productOfferingsStartValidateAction,
} from './wizardProductOfferingsActions';
import {
    makeSelectProductOfferingsDateFrom,
    makeSelectProductOfferingsDateTo,
    makeSelectProductOfferingsIsDisconnect,
    makeSelectProductOfferingsIsTariff,
    makeSelectProductOfferingsRemovedSubscribers,
    makeSelectProductOfferingsTimeFrom,
    makeSelectProductOfferingsTimeTo,
} from './wizardProductOfferingsSelectors';
import { IProductOfferingsSetParamsActionPayload } from './wizardProductOfferingsTypes';

function updateDateRequestFormat(date: string) {
    return moment(date).format(EFormatData.FullData);
}

export function* productOfferingsStepConfirmationInitSaga(action: TWizardMetaSuccessAction): SagaIterator {
    const params: IProductOfferingsSetParamsActionPayload = {
        productOfferings: action.payload.step.productOfferings || [],
        totalMegaPowers: action.payload.step.megaPowers || {},
    };
    yield put(productOfferingsSetParamsAction(params));
    yield call(wizardReportStorageRestoreSaga, action);
}

export function* productOfferingsStepConfirmationHandleNextSaga(): SagaIterator {
    const dateFrom = yield select(makeSelectProductOfferingsDateFrom());
    const dateTo = yield select(makeSelectProductOfferingsDateTo());
    const timeFrom = yield select(makeSelectProductOfferingsTimeFrom());
    const timeTo = yield select(makeSelectProductOfferingsTimeTo());
    const isRoamingProductsFeatureEnabled =
            yield select(makeSelectFeatureEnabled(FeatureNameEnum.ROAMING_PRODUCTS));


    const dataForSend = {};
    if (dateFrom) {
        const dateFromForSend = updateDateRequestFormat(dateFrom);
        set(dataForSend, 'dateFrom', dateFromForSend);
    }
    if (timeFrom && isRoamingProductsFeatureEnabled) {
        set(dataForSend, 'fromTime', timeFrom);
    }
    if (dateTo) {
        const dateToForSend = updateDateRequestFormat(dateTo);
        const isDisconnect = yield select(makeSelectProductOfferingsIsDisconnect());
        if (isDisconnect) {
            set(dataForSend, 'dateFrom', dateToForSend);
            if (isRoamingProductsFeatureEnabled) set(dataForSend, 'fromTime', timeTo);
        } else {
            set(dataForSend, 'dateTo', dateToForSend);
            if (isRoamingProductsFeatureEnabled) set(dataForSend, 'toTime', timeTo);
        }
    }

    const removedSubscribers = yield select(makeSelectProductOfferingsRemovedSubscribers());
    set(dataForSend, 'excludedSubsIds', removedSubscribers);

    const reportParams: IWizardReportOrderParams = yield select(makeSelectWizardReportOrderParams());

    // send business event
    yield put(
        logBusinessEvent({
            eventName: ELogBusinessEventName.USER_PAGE_PRODUCT_MASTER_CREATE_ORDER,
            type: ELogBusinessEventType.common,
        }),
    );

    const pageFrom = yield select(makeSelectProductsPageFrom());

    if (pageFrom === EProductsPageFrom.SUBSCRIBER_ID_CHANGE_MEGA_POWERS) {
        yield put(
            logBusinessEvent({
                eventName: ELogBusinessEventName.USER_MEGA_POWERS,
                type: ELogBusinessEventType.common,
            }),
        );
    }

    return {
        data: dataForSend,
        ...reportParams,
    };
}

export function* productOfferingsStepConfirmationNextSuccessSaga(action: TWizardNextSuccessAction): SagaIterator {
    const orderId = action.payload.currentStep?.step.order?.orderId ?? '';
    const orders = action.payload.currentStep?.step.orders;

    const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
    const goBack = () => breadCrumbGoBack(breadCrumbsData);

    const isDisconnect: boolean = yield select(makeSelectProductOfferingsIsDisconnect());
    const isTariff: boolean = yield select(makeSelectProductOfferingsIsTariff());

    const pageFrom: EProductsPageFrom = yield select(makeSelectProductsPageFrom());

    const isMegaPowersAvailable: boolean =
        action.payload.currentStep?.step.isMegaPowersAvailable ?? false;

    const { orderCreationModal } = yield select(makeSelectTranslationCommon());

    const isMegaPowersFeatureGreaterOrEqualThan2 = yield select(
        makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
    );

    const additionalParamsForOrdersModal: Partial<TModalMultipleOrdersProps> = {};

    if (
        (pageFrom === EProductsPageFrom.SUBSCRIBERS || pageFrom === EProductsPageFrom.HEADER) &&
        isMegaPowersAvailable
    ) {
        additionalParamsForOrdersModal.onLinkClickUrl = RoutePath.products;
        additionalParamsForOrdersModal.ordersStatusLinkTranslation =
            orderCreationModal.success.ordersStatusLinkMegaPowers;
        additionalParamsForOrdersModal.ordersStatusTextTranslation =
            orderCreationModal.success.orderStatusTextMegaPowers;
    }

    if (isMegaPowersFeatureGreaterOrEqualThan2) {
        yield put(
            modalOpen({
                render: () =>
                    createElement(ModalMultipleOrders, {
                        onClose: goBack,
                        onSuccessButtonClick: breadCrumbGoBack,
                        isOpen: true,
                        orderOrOrders: orders ?? orderId,
                        orderTypeTranlation: orderCreationModal.success.orderType.RATE_PLAN_ACTIVATE,
                        ...additionalParamsForOrdersModal,
                    }),
                modalParams: {
                    closeable: false,
                },
            }),
        );
    } else {
        yield put(
            modalOpen({
                onCrossClickCallback: goBack,
                render: (props: IModalRenderProps) =>
                    createElement(ProductOfferingsModalSuccess, {
                        dataName: 'product-offerings-modal-success',
                        isDisconnect,
                        isTariff,
                        isMegaPowersAvailable,
                        onCloseModal: props.onCloseModal,
                        orderId: orders ?? orderId,
                    }),
                modalParams: {
                    closeable: false,
                },
            }),
        );

        // These events are no longer supported
        // yield put(logBusinessEvent({
        //     type: ELogBusinessEventType.wizard,
        //     eventName: isTariff
        //         ? ELogBusinessEventName.B2B_RATE_PLAN_MASTER_CREATE_ORDER
        //         : ELogBusinessEventName.B2B_SERVICES_MASTER_CREATE_ORDER,
        //     orderId,
        // }));
    }
}

export function* openModalWithSuggestInquiry(): SagaIterator {
    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ProductOfferingsModalSuggestInquiry, {
                    dataName: 'product-offerings-modal-suggest-inquiry',
                    onCloseModal: props.onCloseModal,
                }),
        }),
    );
}

export function* productOfferingsCreateInquirySaga(): SagaIterator {
    try {
        const header: string = yield select(makeSelectWizardBasketSnapshotHeader());
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'X-BASKET-SNAPSHOT': header,
            },
        };
        const url = API_HOSTNAME + PRODUCT_OFFERINGS_CREATE_INQUIRY;
        const { campaignInquiryId } = yield call(requestSaga, url, EHttpMethod.POST, null, options);

        yield put(productOfferingsCreateInquirySuccess(campaignInquiryId));
        yield put(wizardFinishAction());

        const breadCrumbsData = yield select(makeSelectBreadCrumbsData());
        const goBack = () => breadCrumbGoBack(breadCrumbsData);
        yield put(
            modalOpen({
                onCrossClickCallback: goBack,
                render: (props: IModalRenderProps) =>
                    createElement(ProductOfferingsModalSuccessInquiry, {
                        dataName: 'product-offerings-modal-success-inquiry',
                        goBack,
                        onCloseModal: props.onCloseModal,
                        campaignInquiryId: campaignInquiryId,
                    }),
                modalParams: {
                    closeable: false,
                },
            }),
        );
    } catch (e: any) {
        const errorCode = e.response?.error?.code;
        if (errorCode) {
            const translationProductOfferings = yield select(makeSelectTranslationWizardProductOfferings());
            const wizardTranslation = yield select(makeSelectWizardTranslation());
            const message =
                translationProductOfferings.error.codes[errorCode] || wizardTranslation.wizardModalError.default;

            yield call(checkResponseErrorWithModal, e, { message });
        }
        yield put(productOfferingsCreateInquiryFailure(e.toString()));
    }
}

export function* productOfferingsGetSubscribersCheckListSaga(): SagaIterator {
    try {
        const header: string = yield select(makeSelectWizardBasketSnapshotHeader());
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'X-BASKET-SNAPSHOT': header,
            },
        };
        const isDisconnect = yield select(makeSelectProductOfferingsIsDisconnect());
        const dateFrom = yield select(makeSelectProductOfferingsDateFrom());
        const dateTo = yield select(makeSelectProductOfferingsDateTo());
        const timeFrom = yield select(makeSelectProductOfferingsTimeFrom());
        const timeTo = yield select(makeSelectProductOfferingsTimeTo());
        const isRoamingProductsFeatureEnabled =
                yield select(makeSelectFeatureEnabled(FeatureNameEnum.ROAMING_PRODUCTS));

        const body = {};
        if (dateFrom) set(body, 'dateFrom', updateDateRequestFormat(dateFrom));
        if (dateTo) {
            if (isDisconnect) {
                set(body, 'dateFrom', updateDateRequestFormat(dateTo));
            } else {
                set(body, 'dateTo', updateDateRequestFormat(dateTo));
            }
        }

        if (timeFrom && isRoamingProductsFeatureEnabled) set(body, 'fromTime', timeFrom);

        if (timeTo && isRoamingProductsFeatureEnabled) {
            if (isDisconnect) {
                set(body, 'fromTime', timeTo)
            } else {
                set(body, 'toTime', timeTo)
            }
        }

        let url = API_HOSTNAME + PRODUCT_OFFERINGS_SUBSCRIBERS_CHECK;

        const wizardName = yield select(makeSelectWizardName());
        if (wizardName === EWizardName.PRODUCT_OFFERINGS_CAMPAIGN) {
            url = API_HOSTNAME + PRODUCT_OFFERINGS_CAMPAIGN_SUBSCRIBERS_CHECK;
        }

        const data = yield call(requestSaga, url, EHttpMethod.POST, body, options);

        yield put(productOfferingsGetSubscribersCheckListSuccess(data));

        yield put(
            productOfferingsSetParamsAction({
                subscriberActivationList: data,
                subsCount: data.length,
            }),
        );
    } catch (e: any) {
        yield put(productOfferingsGetSubscribersCheckListFailure(e.message));
    } finally {
        yield put(productOfferingsStartValidateAction());
    }
}

export function* productOfferingsStepConfirmationUpdateStorageSaga(): SagaIterator {
    const removedSubscribers = yield select(makeSelectProductOfferingsRemovedSubscribers());

    yield put(wizardStorageUpdateStepAction({ removedSubscribers }));
}
